import { Sidebar } from "primereact/sidebar";
import "./amend-dtr.scss";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQyAmendmentEmployeeRequest } from "@core/query/employee-request.query";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useFormDropdownPersonContext } from "@domain/person/form-dropdown-person/form-dropdown-person.context";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import {
  EmployeeRequestFormRule,
  EmployeeRequestFormSchema,
} from "@core/form/form.rule";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { employeeRequestFormDefault } from "@core/form/form.default";
import { useFormDropdownRequestType } from "@domain/request-type/form-dropdown-request-type/form-dropdown-request-type.hook";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import FormEmployeeRequestDtr from "@domain/employee-request/form-employee-request-dtr/form-employee-request-dtr";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import HeaderContent from "@shared/ui/header-content/header-content";
import { Button } from "primereact/button";

export function AmendDtr() {
  const { formatDate, convertToPhDate, formatDateTimeForInputMeridian } =
    useDateTimeContext();
  const [searchParam] = useSearchParams();
  const dtrCode = searchParam.get("code");
  const clockIn = searchParam.get("in");
  const clockOut = searchParam.get("out");
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotificationContext();
  const { selectedPersonData } = useFormDropdownPersonContext();
  const { mapDtrEmployeeRequest } = useFormApiMapper();
  const { getAmendDtrMap } = useFormDropdownRequestType();

  // QUERIES
  const handleApiSuccess = () => {
    showSuccess(`New Employee Request for Amend DTR is created`);
    navigate(`../`);
  };
  const { mutate: dtrRequest, isLoading: isLoadingDtr } =
    useQyAmendmentEmployeeRequest(handleApiSuccess);

  // LOCAL FUNCTIONS
  const clockInPh = convertToPhDate(clockIn)?.toISOString();
  const clockOutPh = convertToPhDate(clockOut)?.toISOString();
  const formMethod = useForm<EmployeeRequestFormSchema>({
    defaultValues: {
      ...employeeRequestFormDefault,
      dtrCode: dtrCode || "",
      amendCurrentClockin: formatDateTimeForInputMeridian(clockInPh, ""),
      amendCurrentClockout: formatDateTimeForInputMeridian(clockOutPh, ""),
      amendClockin: formatDateTimeForInputMeridian(clockInPh, ""),
      amendClockout: formatDateTimeForInputMeridian(clockOutPh, ""),
      workDate: formatDate(clockIn, ""),
    },
    resolver: zodResolver(EmployeeRequestFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: EmployeeRequestFormSchema) => {
    const defaultFormData = {
      userCode: selectedPersonData?.code,
      bucketCode: selectedPersonData?.bucket.code,
    };

    const requestType = getAmendDtrMap()?.value;
    const mutatedForm = {
      ...form,
      userCode: defaultFormData.userCode,
      bucketCode: defaultFormData.bucketCode,
      requestType: requestType,
    } as EmployeeRequestFormSchema;

    const amendmendPayload = mapDtrEmployeeRequest(mutatedForm);
    dtrRequest(amendmendPayload);
  };
  const handleValidateError = (err: FieldErrors<EmployeeRequestFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return (
    <Sidebar
      visible={true}
      onHide={() => navigate("../")}
      className="w-full md:w-[700px]"
    >
      <div id="AmendDtr" className="amend-dtr">
        <HeaderContent
          title="Amend DTR"
          onBack={() => navigate("../")}
          headingType={"h2"}
        >
          <Button
            className="w-full block"
            label="Save"
            disabled={isLoadingDtr}
            onClick={handleSubmit(handleValidate, handleValidateError)}
          ></Button>
        </HeaderContent>

        <section className="p-7">
          <FormProvider {...formMethod}>
            <FormEmployeeRequestDtr omitDtrSelection={true} />
          </FormProvider>
        </section>
      </div>
    </Sidebar>
  );
}

export default AmendDtr;
